import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Dropdown,
  Input,
  Menu,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { masterDataService, Vendor, VendorProductCatalogItem } from '../services/masterDataService';
import VendorProductLinkFormModal, { VendorProductLinkMode } from '../components/VendorProductLinkFormModal';
import { PERMISSIONS, RBACService } from '../utils/rbac';
import './VendorProducts.css';

const { Title, Text } = Typography;
const { Option } = Select;

const CATALOG_FETCH_LIMIT = 5000;

function vendorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase() || '—';
}

function rowStatus(r: VendorProductCatalogItem): 'active' | 'pending' {
  return r.vendor_product_code?.trim() ? 'active' : 'pending';
}

function catalogRowKey(r: VendorProductCatalogItem): string {
  return String(r.id);
}

const VendorProducts: React.FC = () => {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState<VendorProductCatalogItem[]>([]);
  const [fullTotal, setFullTotal] = useState(0);
  const [vendorFilter, setVendorFilter] = useState<number | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending'>('all');
  const [catalogFilterText, setCatalogFilterText] = useState('');
  const [vendorsForFilter, setVendorsForFilter] = useState<Vendor[]>([]);
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: VendorProductLinkMode;
    row: VendorProductCatalogItem | null;
    prefillProductId?: number | null;
  }>({ open: false, mode: 'add', row: null, prefillProductId: undefined });
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  const canWriteCatalog = RBACService.canWriteVendorCatalog();
  const canDeleteCatalogLine = RBACService.canDeleteVendorCatalogLine();

  const loadVendorsForFilter = useCallback(async () => {
    try {
      const list = await masterDataService.getVendors({ active_only: true, limit: 500 });
      setVendorsForFilter(list || []);
    } catch {
      setVendorsForFilter([]);
    }
  }, []);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterDataService.getVendorProductsCatalog({
        skip: 0,
        limit: CATALOG_FETCH_LIMIT,
        active_only: true,
      });
      const vendorLines = res.items || [];
      setAllRows(vendorLines);
      setFullTotal(vendorLines.length);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Failed to load vendor product catalog');
      setAllRows([]);
      setFullTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVendorsForFilter();
  }, [loadVendorsForFilter]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const categoryOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of allRows) {
      const n = r.product?.category?.name;
      if (n) names.add(n);
    }
    return Array.from(names).sort();
  }, [allRows]);

  const filteredRows = useMemo(() => {
    let list = allRows;
    if (vendorFilter) {
      list = list.filter((r) => r.vendor.id === vendorFilter);
    }
    if (categoryFilter) {
      list = list.filter((r) => r.product?.category?.name === categoryFilter);
    }
    if (statusFilter === 'active') {
      list = list.filter((r) => r.vendor_product_code?.trim());
    }
    if (statusFilter === 'pending') {
      list = list.filter((r) => !r.vendor_product_code?.trim());
    }
    const q = catalogFilterText.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          r.vendor.name.toLowerCase().includes(q) ||
          r.vendor.code.toLowerCase().includes(q) ||
          r.name.toLowerCase().includes(q) ||
          (r.product?.name || '').toLowerCase().includes(q) ||
          (r.product?.code || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [allRows, vendorFilter, categoryFilter, statusFilter, catalogFilterText]);

  const filteredTotal = filteredRows.length;

  useEffect(() => {
    if (deepLinkHandled || loading || !canWriteCatalog) return;
    const params = new URLSearchParams(location.search);
    const raw = params.get('link_product_id');
    if (!raw) return;
    const productId = Number(raw);
    if (!Number.isFinite(productId) || productId <= 0) {
      setDeepLinkHandled(true);
      navigate('/vendor-products', { replace: true });
      return;
    }
    const alreadyLinked = allRows.some((r) => !r.is_company_master_row && r.product?.id === productId);
    if (alreadyLinked) {
      message.info('This product already has a vendor link. You can edit it from this page.');
    } else {
      openAddLinkMaster(productId);
    }
    setDeepLinkHandled(true);
    navigate('/vendor-products', { replace: true });
  }, [location.search, deepLinkHandled, loading, canWriteCatalog, allRows, navigate]);

  const openAdd = () => setFormModal({ open: true, mode: 'add', row: null, prefillProductId: null });
  const openAddLinkMaster = (productId: number) =>
    setFormModal({ open: true, mode: 'add', row: null, prefillProductId: productId });
  const openView = (row: VendorProductCatalogItem) =>
    setFormModal({ open: true, mode: 'view', row, prefillProductId: undefined });
  const openEdit = (row: VendorProductCatalogItem) =>
    setFormModal({ open: true, mode: 'edit', row, prefillProductId: undefined });
  const closeForm = () => setFormModal({ open: false, mode: 'add', row: null, prefillProductId: undefined });

  const openViewMaster = (r: VendorProductCatalogItem) => {
    const p = r.product;
    if (!p) return;
    Modal.info({
      title: `Company product — ${p.name}`,
      width: 520,
      content: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <Text strong>Code: </Text>
            <Text className="app-mono">{p.code}</Text>
          </div>
          {p.category?.name ? (
            <div>
              <Text strong>Category: </Text>
              <Text>{p.category.name}</Text>
            </div>
          ) : null}
          <Text type="secondary">
            This item is in your company master catalog but has no supplier line yet. Use &quot;Link supplier&quot; to add
            pricing and SKU for a vendor.
          </Text>
        </Space>
      ),
    });
  };

  const handleDelete = async (row: VendorProductCatalogItem) => {
    if (row.is_company_master_row || !canDeleteCatalogLine) return;
    try {
      await masterDataService.removeVendorProduct(row.vendor.id, row.id);
      message.success('Removed from vendor catalog');
      fetchCatalog();
    } catch {
      // 4xx/5xx: global axios interceptor already surfaces detail (avoid duplicate toasts, e.g. 409 PO reference)
    }
  };

  const columns: ColumnsType<VendorProductCatalogItem> = [
    {
      title: 'Vendor',
      key: 'vendor',
      width: 240,
      render: (_, r) =>
        r.is_company_master_row ? (
          <div className="vp-vendor-cell">
            <div className="vp-avatar" aria-hidden style={{ background: '#f0f0f0', color: '#595959' }}>
              —
            </div>
            <div>
              <div className="vp-cell-title" style={{ color: '#595959' }}>
                Company master
              </div>
              <div className="vp-cell-sub">No supplier line yet</div>
            </div>
          </div>
        ) : (
          <div className="vp-vendor-cell">
            <div className="vp-avatar" aria-hidden>
              {vendorInitials(r.vendor.name)}
            </div>
            <div>
              <div className="vp-cell-title">{r.vendor.name}</div>
              <div className="vp-cell-sub app-mono">{r.vendor.code}</div>
            </div>
          </div>
        ),
    },
    {
      title: 'Vendor product',
      key: 'vendor_line',
      render: (_, r) => (
        <div>
          <div className="vp-cell-title">{r.name}</div>
          <div className="vp-cell-sub app-mono">
            {r.vendor_product_code?.trim() ? r.vendor_product_code : '—'}
          </div>
          {r.is_company_master_row && r.product ? (
            <>
              <div className="vp-cell-sub" style={{ marginTop: 4 }}>
                Master: {r.product.code}
              </div>
              {r.product.category?.name ? (
                <span className="vp-product-cat">{r.product.category.name}</span>
              ) : null}
            </>
          ) : r.product ? (
            <>
              <div className="vp-cell-sub" style={{ marginTop: 4 }}>
                Linked: {r.product.name} ({r.product.code})
              </div>
              {r.product.category?.name ? (
                <span className="vp-product-cat">{r.product.category.name}</span>
              ) : null}
            </>
          ) : (
            <span className="vp-product-cat">Not linked to company product</span>
          )}
        </div>
      ),
    },
    {
      title: 'Vendor SKU',
      dataIndex: 'vendor_product_code',
      width: 130,
      render: (v: string | null | undefined) => (
        <span className="vp-sku-pill">{v?.trim() ? v : '—'}</span>
      ),
    },
    {
      title: 'Vendor price',
      dataIndex: 'vendor_price',
      width: 120,
      align: 'right',
      render: (v: number, r: VendorProductCatalogItem) =>
        r.is_company_master_row ? (
          <span className="vp-price" style={{ color: '#8c8c8c' }}>
            —
          </span>
        ) : (
          <span className="vp-price">Nu. {Number(v).toLocaleString()}</span>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, r) => {
        if (r.is_company_master_row) {
          return (
            <span className="vp-status-pill vp-status-pill--pending">
              <span className="vp-status-dot" />
              Master only
            </span>
          );
        }
        const s = rowStatus(r);
        return (
          <span className={s === 'active' ? 'vp-status-pill vp-status-pill--active' : 'vp-status-pill vp-status-pill--pending'}>
            <span className="vp-status-dot" />
            {s === 'active' ? 'Active' : 'Pending'}
          </span>
        );
      },
    },
    {
      title: 'Operations',
      key: 'operations',
      width: 120,
      align: 'center',
      className: 'vp-table-col-actions',
      render: (_, r) => (
        <Dropdown
          overlay={
            <Menu
              selectable={false}
              onClick={({ key, domEvent }) => {
                domEvent?.stopPropagation();
                if (r.is_company_master_row) {
                  if (key === 'view') openViewMaster(r);
                  else if (key === 'link' && r.product?.id != null) openAddLinkMaster(r.product.id);
                  return;
                }
                if (key === 'view') openView(r);
                else if (key === 'edit') openEdit(r);
                else if (key === 'delete') {
                  Modal.confirm({
                    title: 'Remove this product from the vendor catalog?',
                    content:
                      'This cannot be undone. If this supplier line is used on any purchase order, removal will be blocked until those orders no longer reference it.',
                    okText: 'Remove',
                    okType: 'danger',
                    cancelText: 'Cancel',
                    onOk: () => handleDelete(r),
                  });
                }
              }}
            >
              <Menu.Item key="view" icon={<EyeOutlined />}>
                View
              </Menu.Item>
              {r.is_company_master_row
                ? canWriteCatalog && r.product?.id != null
                  ? (
                      <Menu.Item key="link" icon={<LinkOutlined />}>
                        Link supplier
                      </Menu.Item>
                    )
                  : null
                : canWriteCatalog ? (
                    <Menu.Item key="edit" icon={<EditOutlined />}>
                      Edit
                    </Menu.Item>
                  ) : null}
              {!r.is_company_master_row && canDeleteCatalogLine ? (
                <>
                  {canWriteCatalog ? <Menu.Divider /> : null}
                  <Menu.Item key="delete" danger icon={<DeleteOutlined />}>
                    Delete
                  </Menu.Item>
                </>
              ) : null}
            </Menu>
          }
          trigger={['click']}
          placement="bottomRight"
          getPopupContainer={() => document.body}
        >
          <Button
            size="small"
            type="primary"
            ghost
            className="po-action-manage"
            aria-label="Manage vendor product row"
            onClick={(e) => e.stopPropagation()}
          >
            Manage <DownOutlined />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const hasActiveFilters =
    !!catalogFilterText.trim() || vendorFilter != null || categoryFilter != null || statusFilter !== 'all';

  return (
    <div className="purchase-orders-page vendor-products-page" style={{ minHeight: '100%', overflowY: 'auto', position: 'relative' }}>
      <div className="vp-header-shell">
        <div className="vp-header">
          <div className="vp-header__icon" aria-hidden>
            <span className="vp-header__emoji">📦</span>
          </div>
          <div>
            <Title level={2} className="vp-header__title">Vendor Products</Title>
            <Text className="vp-header__subtitle">Supplier catalog lines linked to vendors</Text>
          </div>
        </div>

        <div className="vp-header-controls">
          <Input
            className="vp-search-input"
            placeholder="Search vendor, product..."
            allowClear
            value={catalogFilterText}
            onChange={(e) => setCatalogFilterText(e.target.value)}
          />
          <Select
            allowClear
            placeholder="All vendors"
            className="vp-select vp-select--vendor"
            value={vendorFilter}
            onChange={(v) => setVendorFilter(v)}
            showSearch
            optionFilterProp="children"
          >
            {vendorsForFilter.map((v) => (
              <Option key={v.id} value={v.id}>
                {v.name} ({v.code})
              </Option>
            ))}
          </Select>
          {canWriteCatalog && (
            <Button type="primary" className="vp-add-btn" icon={<PlusOutlined />} onClick={openAdd}>
              Add Vendor Product
            </Button>
          )}
        </div>

        <div className="vp-header-filters">
          <Select
            allowClear
            placeholder="All categories"
            className="vp-select"
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v)}
          >
            {categoryOptions.map((c) => (
              <Option key={c} value={c}>
                {c}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="All status"
            className="vp-select"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v || 'all')}
          >
            <Option value="all">All status</Option>
            <Option value="active">Active</Option>
            <Option value="pending">Pending</Option>
          </Select>
        </div>
      </div>

      <Card
        className="page-table-card page-table-card--blue vendor-products-table-card"
        bodyStyle={{
          overflow: 'visible',
          padding: 0,
          position: 'relative',
          minHeight: isMobile ? '300px' : '400px',
          display: 'flex',
          flexDirection: 'column',
          border: 'none',
        }}
        style={{
          position: 'relative',
          zIndex: 1,
          minHeight: isMobile ? '300px' : '400px',
          display: 'flex',
          flexDirection: 'column',
          border: 'none',
          boxShadow: 'none',
        }}
      >
        {filteredTotal === 0 && !loading ? (
          <div
            className="empty-state"
            style={{
              padding: '60px 20px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div className="empty-state-icon">🏷️</div>
            <div className="empty-state-title">No vendor products found</div>
            <div className="empty-state-description">
              {hasActiveFilters ? 'Try adjusting your search or filter criteria' : 'Add a vendor product or link a supplier line to a company product'}
            </div>
            {canWriteCatalog && !hasActiveFilters && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} size="large" style={{ marginTop: 16 }}>
                Add Vendor Product
              </Button>
            )}
          </div>
        ) : (
          <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Table<VendorProductCatalogItem>
              rowKey={catalogRowKey}
              loading={loading}
              columns={columns}
              dataSource={filteredRows}
              scroll={{
                x: isMobile ? 1100 : 1200
              }}
              pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                showSizeChanger: !isMobile,
                pageSizeOptions: ['5', '10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} lines`,
                responsive: true,
                position: ['bottomCenter'],
              }}
              size={isMobile ? 'small' : 'middle'}
            />
          </div>
        )}
      </Card>

      {fullTotal > CATALOG_FETCH_LIMIT && (
        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
          Showing up to {CATALOG_FETCH_LIMIT} catalog rows for filters and stats. Totals reflect the full catalog when
          reported by the server.
        </Text>
      )}

      <VendorProductLinkFormModal
        open={formModal.open}
        mode={formModal.mode}
        row={formModal.row}
        vendors={vendorsForFilter}
        prefillProductId={formModal.prefillProductId ?? undefined}
        onClose={closeForm}
        onSuccess={fetchCatalog}
      />
    </div>
  );
};

export default VendorProducts;
